import React from "react";
import { Overflow, OverflowItem, Tooltip, Button, useIsOverflowItemVisible, } from "@fluentui/react-components";
import { isCapabilityEnabled } from "owa-capabilities";
import { headerFullBleedCapability } from "owa-capabilities-definitions/lib/headerFullBleedCapability";
import classNames from "owa-classnames";
import { isDrawerPaneVisible } from "owa-drawer";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";
import { HEADER_BUTTONS_REGION_ID } from "owa-header-constants";
import type { OwaHeaderButtonControl, OwaHeaderButtonControls } from "owa-header-types";
import { observer } from "owa-mobx-react";
import { safeRequestAnimationFrame } from "owa-performance";
import { useComputedValue } from "owa-react-hooks/lib/useComputed";
import useButtonGroupContainerStyle from "../../hooks/useButtonGroupContainerStyle";
import useCloseHiddenHeaderPanes from "../../hooks/useCloseHiddenHeaderPanes";
import useHeaderButtonVisibility from "../../hooks/useHeaderButtonVisibility";
import toggleHeaderPane from "../../orchestration/toggleHeaderPane";
import logHeaderButtonClick from "../../utils/logHeaderButtonClick";
import { BUTTON_WIDTH } from "../../constants/constants";
import OwaHeaderButtonBadge from "./OwaHeaderButtonBadge";
import OwaHeaderOverflowMenu from "./OwaHeaderOverflowMenu";
import { activeButton, activeFilledOnHoverButton, buttonBadge, buttonFullBleed, filledOnHoverButton, headerButtonFlat, buttonIcon, headerButton, headerButtonGroup, headerButtonGroupFlat, headerButtonWrapper, overflowContainer, overflowContainerFlat, buttonLabel, wideContentButton, wideContentIcon, } from "./OwaHeaderButtonGroup.scss";

interface OwaHeaderButtonGroupProps {
    buttons: OwaHeaderButtonControls;
    regionId?: string;
}

interface OverflowAwareHeaderButtonProps {
    id: string;
    priority: number;
    visibilityContextKey?: string;
    onShow?: () => void;
    onHeaderVisibilityChange?: (isVisible: boolean) => void;
    children: React.ReactElement;
}

export function getReservedExtraWidth(buttons: OwaHeaderButtonControls): number {
    return buttons.reduce((sum, button) => {
        const resolved = typeof button.contentWidth === "function" ? button.contentWidth() : button.contentWidth;
        const extraContentWidth = Math.max(0, (resolved ?? BUTTON_WIDTH) - BUTTON_WIDTH);
        return sum + extraContentWidth + Math.max(0, button.headerSpacingBefore ?? 0);
    }, 0);
}

const defaultExport: React.FunctionComponent<OwaHeaderButtonGroupProps> = observer(function OwaHeaderButtonGroup({ buttons, regionId = HEADER_BUTTONS_REGION_ID }) {
    // Account-scoped buttons (e.g. the consumer-only Rewards button) read the module-context
    // mailbox inside their `isVisible()`. Because this component is an `observer` and
    // useHeaderButtonVisibility evaluates visibility via a MobX `computed`, those buttons toggle
    // on an in-app account switch without a reload — no explicit account dependency needed.
    const visibleButtons = useHeaderButtonVisibility(buttons);
    useCloseHiddenHeaderPanes(buttons, visibleButtons);
    const reservedSlotButtons = React.useMemo(() => visibleButtons.filter(button => button.overflowItem?.addsHeaderSlot), [visibleButtons]);
    const reservedExtraWidth = useComputedValue(() => getReservedExtraWidth(reservedSlotButtons), [reservedSlotButtons]);
    const containerStyle = useButtonGroupContainerStyle(visibleButtons.length, reservedSlotButtons.length, reservedExtraWidth);
    
    if (isWin32StylingEnabled()) {
        return (<div className={headerButtonGroupFlat}>
                    <div id={regionId} className={overflowContainerFlat}>
                        {visibleButtons.map(button => (<OwaHeaderButton key={button.id} flat={true} {...button}/>))}
                    </div>
                </div>);
    }
    
    return (<div style={containerStyle} className={headerButtonGroup}>
                <Overflow overflowDirection="start">
                    <div id={regionId} className={overflowContainer}>
                        {visibleButtons.map(button => (<OwaHeaderButton key={button.id} {...button}/>))}
                        <OwaHeaderOverflowMenu buttons={visibleButtons}/>
                    </div>
                </Overflow>
            </div>);
}, "OwaHeaderButtonGroup");

const OverflowAwareHeaderButton: React.FC<OverflowAwareHeaderButtonProps> = ({ id, priority, visibilityContextKey, onShow, onHeaderVisibilityChange, children, }) => {
    const isVisible = useIsOverflowItemVisible(id);
    
    React.useEffect(() => {
        if (!isVisible) {
            onHeaderVisibilityChange?.(false);
            return;
        }
        onShow?.();
        
        if (!onHeaderVisibilityChange) {
            return;
        }
        
        const notifyHeaderVisibilityChange = onHeaderVisibilityChange;
        let animationFrameId: number | undefined;
        let isDisposed = false;
        
        function onVisibilityChange() {
            if (document.visibilityState === "visible") {
                document.removeEventListener("visibilitychange", onVisibilityChange);
                scheduleVisibilityCheck();
            }
        }
        
        function scheduleVisibilityCheck() {
            animationFrameId = safeRequestAnimationFrame(isPageVisible => {
                animationFrameId = undefined;
                if (isDisposed) {
                    return;
                }
                if (isPageVisible) {
                    notifyHeaderVisibilityChange(true);
                }
                else if (document.visibilityState === "visible") {
                    scheduleVisibilityCheck();
                }
                else {
                    document.addEventListener("visibilitychange", onVisibilityChange);
                }
            });
        }
        
        scheduleVisibilityCheck();
        return () => {
            isDisposed = true;
            document.removeEventListener("visibilitychange", onVisibilityChange);
            if (animationFrameId !== undefined) {
                window.cancelAnimationFrame(animationFrameId);
            }
            notifyHeaderVisibilityChange(false);
        };
    }, [isVisible, visibilityContextKey, onShow, onHeaderVisibilityChange]);
    
    return (<OverflowItem id={id} priority={priority}>
            {children}
        </OverflowItem>);
};

export const OwaHeaderButton: React.FC<OwaHeaderButtonControl & {
    flat?: boolean;
}> = observer(({ id, renderLabel, renderIcon, renderTooltip, showLabel, onClick, disabled, onNonPointerActivate, onShow, onHeaderVisibilityChange, headerVisibilityContextKey, onHover, pane, badge, overflowItem, contentWidth, fixedContentWidth, headerSpacingBefore, flat, }) => {
    const visibilityContextKey = headerVisibilityContextKey?.();
    
    React.useEffect(() => {
        if (!flat) {
            return;
        }
        
        onShow?.();
        onHeaderVisibilityChange?.(true);
        return () => onHeaderVisibilityChange?.(false);
    }, [flat, visibilityContextKey, onShow, onHeaderVisibilityChange]);
    
    const handleClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        logHeaderButtonClick(id, "header");
        if (event.detail === 0 && onNonPointerActivate) {
            onNonPointerActivate("header");
        }
        else {
            onClick?.("header");
        }
        if (pane) {
            toggleHeaderPane(pane.id);
        }
    }, [id, onClick, onNonPointerActivate, pane]);
    
    const shouldShowLabel = typeof showLabel === "function" ? showLabel() : showLabel;
    const resolvedContentWidth = typeof contentWidth === "function" ? contentWidth() : contentWidth;
    const shouldUseFixedContentWidth = typeof fixedContentWidth === "function" ? fixedContentWidth() : fixedContentWidth;
    
    const isActive = useComputedValue(() => {
        return pane ? isDrawerPaneVisible(pane.id) : false;
    }, [pane?.id]);
    
    const icon = useComputedValue(() => {
        return (<div className={classNames(buttonIcon, resolvedContentWidth != null && wideContentIcon)}>
                    {renderIcon("header")}
                    {badge?.getCount && (<OwaHeaderButtonBadge id={id} getCount={badge.getCount} display={badge.display} className={classNames(buttonBadge, badge.className)}/>)}
                </div>);
    }, [
        renderIcon,
        id,
        badge?.getCount,
        badge?.display,
        badge?.className,
        resolvedContentWidth,
    ]);
    
    const tooltip = renderTooltip ? renderTooltip() : renderLabel();
    // When the label is shown inline, use it as the accessible name so assistive technology
    // announces the same text that's visible (WCAG 2.5.3 Label in Name); otherwise fall back to
    // the tooltip for icon-only buttons.
    const accessibleName = badge && badge.getCount() > 0 && badge.renderAccessibleName
        ? badge.renderAccessibleName()
        : shouldShowLabel
            ? renderLabel()
            : tooltip;
    const spacingBefore = Math.max(0, headerSpacingBefore ?? 0);
    const buttonStyle = useComputedValue(() => {
        if (resolvedContentWidth == null && (!flat || spacingBefore === 0)) {
            return undefined;
        }
        // Labeled buttons (e.g. Buy Microsoft 365) size to their content and treat
        // `contentWidth` as a maximum, so localized labels of differing lengths render without a
        // per-locale width calculation and truncate with an ellipsis when they exceed the cap.
        // Icon/widget buttons (e.g. Rewards) use the resolved width so their glyph stays centered.
        return {
            ...(resolvedContentWidth == null
                ? undefined
                : shouldShowLabel && !shouldUseFixedContentWidth
                    ? { maxWidth: resolvedContentWidth }
                    : {
                        width: resolvedContentWidth,
                        minWidth: resolvedContentWidth,
                    }),
            ...(shouldUseFixedContentWidth && resolvedContentWidth != null
                ? { flexShrink: 0 }
                : undefined),
            ...(flat && spacingBefore > 0 ? { marginInlineStart: spacingBefore } : undefined),
        };
    }, [
        flat,
        resolvedContentWidth,
        shouldShowLabel,
        shouldUseFixedContentWidth,
        spacingBefore,
    ]);
    
    const wrapperStyle = useComputedValue(() => (spacingBefore > 0 ? { paddingInlineStart: spacingBefore } : undefined), [spacingBefore]);
    
    const priority = overflowItem?.priority ?? 0;
    
    const button = (<Tooltip content={tooltip} relationship="description">
                <Button id={id} aria-label={accessibleName} aria-expanded={pane ? isActive : undefined} className={classNames(headerButton, flat && headerButtonFlat, isCapabilityEnabled(headerFullBleedCapability) && buttonFullBleed, filledOnHoverButton, isActive && activeFilledOnHoverButton, isActive && activeButton, resolvedContentWidth != null && wideContentButton)} style={buttonStyle} icon={icon} disabled={disabled} onClick={handleClick} onMouseEnter={onHover}>
                    {shouldShowLabel ? (<span className={buttonLabel}>{renderLabel()}</span>) : undefined}
                </Button>
            </Tooltip>);
    
    const overflowButton = spacingBefore > 0 ? (<div className={headerButtonWrapper} style={wrapperStyle}>
                    {button}
                </div>) : (button);
    
    return (<>
                {flat ? (button) : onShow || onHeaderVisibilityChange ? (<OverflowAwareHeaderButton id={id} priority={priority} visibilityContextKey={visibilityContextKey} onShow={onShow} onHeaderVisibilityChange={onHeaderVisibilityChange}>
                        {overflowButton}
                    </OverflowAwareHeaderButton>) : (<OverflowItem key={id} id={id} priority={priority}>
                        {overflowButton}
                    </OverflowItem>)}
                {pane?.render && pane.render(pane.id)}
            </>);
}, "OwaHeaderButton");

export default defaultExport;
